<template>
  <dialog ref="dialogRef" class="report-dialog" @close="handleClose" @click="handleBackdropClick">
    <div class="report-card" :class="{ mobile: isMobile }">
      <div class="report-title">
        {{ localize(getReportTitle()) }}
      </div>

      <div v-if="errorMessage" class="error-message">
        <gam-button-message v-bind="errorMessage" />
      </div>

      <div v-if="reportItems && gamForm" class="report-items">
        <gam-switch
          v-for="(item, i) in reportItems"
          :key="`report-item-${i}`"
          v-bind="item"
          v-model="gamForm.form.reason"
        />
      </div>

      <div v-if="gamForm" class="other-reason">
        <gam-textarea
          ref="textareaRef"
          v-model="gamForm.form.message"
          placeholder="dialog.report.other.textarea"
          :value="gamForm.form.message"
        />
      </div>

      <div class="report-actions">
        <button class="report-submit" :disabled="isInvalid || isLoading" @click="handleSubmit">
          {{ localize('dialog.report.button.label') }}
        </button>
      </div>

      <div class="report-close" @click="handleClose">
        <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { BaseReportReason } from '@/core/data/report/report.type';
import { gambits, localize } from '@/core/gambits';
import FormHelper from '@/core/helpers/form.helper';
import ReportHelper from '@/core/helpers/report.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useFormStore } from '@/stores/form.store';
import GamButtonMessage from '@/views/components/GamButtonMessage.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import GamSwitch from '@/views/components/GamSwitch.vue';
import GamTextarea from '@/views/components/GamTextarea.vue';
import { GamButtonMessageVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { FormId } from '@/views/composables/constants/form.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import type { GamButtonMessageType } from '@/views/composables/models/components/GamButton';
import type { GamSwitchType } from '@/views/composables/models/components/GamSwitch';
import type { ReportDialogType } from '@/views/composables/models/dialog.interface';
import type { AddReportForm, AddReportValidator } from '@/views/composables/models/form.interface';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';

const formId = FormId.ADD_REPORT;
const props = defineProps<ReportDialogType>();
const emits = defineEmits([GamComponentsEmits.CLOSE]);

const formStore = useFormStore<AddReportForm, AddReportValidator>(formId)();
const { gamForm } = storeToRefs(formStore);

const dialogRef = ref<HTMLDialogElement>();
const textareaRef = ref<InstanceType<typeof GamTextarea>>();
const reportItems = ref<GamSwitchType[]>();
const isLoading = ref<boolean>(false);
const errorMessage = ref<GamButtonMessageType | null>(null);

const reportValidator = (message: string | null) => {
  return FormHelper.reportValidator(message, gamForm.value?.form.reason);
};

onBeforeMount(() => {
  reportItems.value = ReportHelper.getReportReasons(props.type);
});

const getForm = reactive({
  reason: undefined,
  message: null,
});

const getRules = {
  reason: { required },
  message: { reportValidator },
};

onMounted(() => {
  formStore.setForm({ form: getForm, rules: getRules });
  dialogRef.value?.showModal();
});

onBeforeUnmount(() => {
  if (dialogRef.value?.open) dialogRef.value.close();
});

const v$ = useVuelidate(getRules, getForm);

const isInvalid = computed((): boolean => {
  if (!gamForm.value) return true;
  return v$.value.$invalid;
});

watch(
  () => gamForm.value?.form.reason,
  (reason) => {
    if (reason === BaseReportReason.OTHER) {
      nextTick(() => {
        const el = textareaRef.value?.$el?.querySelector('textarea');
        el?.focus();
      });
    }
  },
);

watch(
  () => gamForm.value?.form.message,
  (msg) => {
    if (msg?.trim() && !gamForm.value?.form.reason) {
      gamForm.value!.form.reason = BaseReportReason.OTHER;
    }
  },
);

const getReportTitle = (): string => {
  return `dialog.report.${props.type}.title`;
};

const handleClose = (e?: Event): void => {
  e?.preventDefault();
  emits(GamComponentsEmits.CLOSE);
};

const handleBackdropClick = (e: MouseEvent): void => {
  if (e.target === dialogRef.value) {
    emits(GamComponentsEmits.CLOSE);
  }
};

const handleReportError = (error: any): void => {
  errorMessage.value = null;

  if (error && error.code === 400) {
    let errorText = error.message || 'An error occurred while submitting the report';

    if (
      error.message?.toLowerCase().includes('already reported') ||
      error.message?.toLowerCase().includes('already exists') ||
      error.errorKey?.includes('already_reported')
    ) {
      errorText = localize('dialog.report.error.already_reported') || 'This item has already been reported';
    }

    errorMessage.value = {
      label: errorText,
      variant: GamButtonMessageVariant.ERROR,
    };

    setTimeout(() => {
      if (errorMessage.value) {
        errorMessage.value = null;
      }
    }, 5000);
  } else if (error) {
    errorMessage.value = {
      label: error.message || 'An error occurred while submitting the report',
      variant: GamButtonMessageVariant.ERROR,
    };

    setTimeout(() => {
      if (errorMessage.value) {
        errorMessage.value = null;
      }
    }, 5000);
  }
};

const handleSubmit = async () => {
  if (gamForm.value?.form && !isLoading.value) {
    isLoading.value = true;
    errorMessage.value = null;

    const result = await gambits.reportService.addReport(props.id, props.type, gamForm.value.form, props.blobSource);

    if (result.data) {
      useToast().success(localize('dialog.report.sent'));
      emits(GamComponentsEmits.CLOSE);
    } else if (result.error) {
      handleReportError(result.error);
    }

    isLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.report-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.report-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-xxl) var(--spacing-xl);
  padding-bottom: 6.5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  max-width: 335px;
  width: 100%;
  gap: var(--spacing-md);

  &.mobile {
    max-width: 88vw;
  }
}

.report-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-white);
  text-align: center;
  padding-bottom: var(--spacing-xl);
}

.error-message {
  margin-bottom: var(--spacing-md);
}

.report-items {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  align-self: stretch;
}

.report-actions {
  margin-top: var(--spacing-md);
  width: 100%;
}

.report-submit {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  background: var(--color-linear-gradient);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
  transition: opacity 200ms ease;

  @include hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.report-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    color: var(--color-white);
    border-color: var(--color-white-20);
  }

  @include press($transform: translateX(-50%));
}
</style>
